<template>
    <collapsible-block
        v-if="!isHidden"
        ref="collapsible"
        :title-text="fileUploadTitle"
        :disabled="!isEdit && !isPreview"
        :collapsed="!isEdit && !isPreview"
        class="business-order-view-modal__shipping-documents"
    >
        <flex-row v-if="!isPreview"
                  column-spacing="lg"
                  class="flex--no-wrap"
        >
            <span class="business-order-view-modal__upload-description">
                {{ fileUploadDescription }}
            </span>
        </flex-row>
        <flex-row v-show="documents.length > 0"
                  column-spacing="lg"
                  class="business-order-view-modal__shipping-documents-row"
        >
            <business-orders-file
                v-for="(document, index) in documents"
                :key="index"
                :file-title="document.fileName"
                :is-preview="isPreview"
                :order-id="orderId"
                :document-id="document.documentId"
                @removed="handleDocumentRemove"
            />
        </flex-row>
        <flex-row v-if="$hasAccess('businessorders:write')"
                  v-show="!showDocumentUpload && !isPreview && !fileLimitReached"
        >
            <div class="col col--on-left">
                <base-button class="business-order-view-modal__shipping-document-action"
                             as-link
                             @click="toggleDocumentUploadVisibility"
                >
                    <svg slot="iconLeft"
                         width="20"
                         height="20"
                    >
                        <use xlink:href="#plus-round" />
                    </svg>
                    {{ $t('businessOrders.labels.addAdditionalDocument') }}
                </base-button>
            </div>
        </flex-row>

        <div v-if="$hasAccess('businessorders:write')"
             v-show="showDocumentUpload"
        >
            <flex-row
                column-spacing="lg"
                class="flex--no-wrap flex--justify-center"
            >
                <drop-zone ref="dropzone"
                           :dropzone-options="documentDropzoneOptions"
                           :placeholder-title="$t('businessOrders.labels.dropPdf')"
                           @file-added="handleFileAdded"
                           @error="handleDropzoneErrors"
                           @canceled="handleDropzoneFileCancelation"
                           @sending="handleSendingDocuments"
                           @success="handleSucceededDocuments"
                           @duplicate="handleDuplicateDocument"
                           @max-files-reached="handleMaxFilesReached"
                />
            </flex-row>
        </div>
    </collapsible-block>
</template>

<script>
import '@/assets/icons/plus-round.svg';
import CollapsibleBlock from '@/components/layout/CollapsibleBlock';
import DropZone from '@/components/drop-zone/DropZone';
import BaseButton from '@/components/base/BaseButton';
import BusinessOrdersFile from './BusinessOrdersFile';

export default {
    components: {
        BusinessOrdersFile,
        CollapsibleBlock,
        DropZone,
        BaseButton,
    },
    props: {
        dropzoneOptions: {
            type: Object,
            default: () => ({}),
        },
        fileUploadTitle: {
            type: String,
            default: '',
        },
        fileUploadDescription: {
            type: String,
            default: '',
        },
        isEdit: {
            type: Boolean,
            default: false,
        },
        isPreview: {
            type: Boolean,
            default: false,
        },
        orderId: {
            type: Number,
            required: true,
        },
        fileType: {
            type: Number,
            required: true,
        },
        shippingDocuments: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            isDocumentUploadVisible: this.shippingDocuments.length === 0,
            documents: this.shippingDocuments || [],
            documentsInProgress: [],
            documentDropzoneOptions: {
                ...this.dropzoneOptions,
                url: this.dropzoneOptions.url + this.fileType,
            },
            maxFileNameLength: 50,
        };
    },
    computed: {
        showDocumentUpload() {
            if (this.isPreview || (this.fileLimitReached && this.documentsInProgress.length === 0)) {
                return false;
            }

            return this.documents.length === 0 || this.isDocumentUploadVisible || this.documentsInProgress.length > 0;
        },
        isHidden() {
            return this.isPreview && this.documents.length === 0;
        },
        documentAmount() {
            return this.documents.length + this.documentsInProgress.length;
        },
        fileLimitReached() {
            return this.documentAmount >= this.dropzoneOptions.maxFiles;
        },
    },
    methods: {
        handleDocumentRemove(documentId) {
            this.documents = this.documents.filter(x => x.documentId !== documentId);
        },
        toggleDocumentUploadVisibility() {
            this.isDocumentUploadVisible = true;
        },
        async handleFileAdded(file) {
            const newFileAdded = {
                title: file.name,
                type: this.fileType,
            };

            if (this.isFilenameTooLong(file.name)) {
                this.$refs.dropzone.removeFile(file);
                this.$emit('max-file-length', this.maxFileNameLength);
            }

            if (!this.isFilenameValid(file.name)) {
                this.$refs.dropzone.removeFile(file);
                this.$emit('invalid-file-name');
            }

            if (this.fileLimitReached) {
                this.documentsInProgress = [];
                await this.$nextTick();
                await this.$refs.dropzone.removeAllFiles();
                this.$emit('max-files-reached', this.dropzoneOptions.maxFiles - this.documentAmount);
                return;
            }

            if (this.isADuplicate(file.name)) {
                this.$refs.dropzone.removeFile(file);
                this.$emit('duplicate');
            } else {
                this.documentsInProgress.push(newFileAdded);
            }
        },
        handleDropzoneErrors(file, message) {
            const index = this.documentsInProgress.findIndex(document => document.title === file.name);
            if (index > -1) {
                this.documentsInProgress.splice(index, 1);
            }

            this.$refs.dropzone.removeFile(file);
            this.$emit('error', file, message);
        },
        handleDropzoneFileCancelation(file) {
            const index = this.documentsInProgress.findIndex(document => document.title === file.name);
            if (index > -1) {
                this.documentsInProgress.splice(index, 1);
            }
        },
        handleSendingDocuments(file) {
            this.$emit('sending', file);
        },
        async handleSucceededDocuments(file, response) {
            const index = this.documentsInProgress.findIndex(document => document.title === file.name);
            if (index > -1) {
                const uploadedDocument = {
                    documentId: response.id,
                    type: this.type,
                    fileName: file.name,
                };
                this.documents.push(uploadedDocument);
                this.documentsInProgress.splice(index, 1);
            }

            this.$refs.dropzone.removeFile(file);
        },
        handleDuplicateDocument() {
            this.$emit('duplicate');
        },
        handleMaxFilesReached() {
            this.$emit('max-files-reached');
        },
        isADuplicate(newDocumentName) {
            return this.documents.filter(document => document.fileName === newDocumentName).length > 0
                || this.documentsInProgress.filter(document => document.fileName === newDocumentName).length > 0;
        },
        isFilenameTooLong(fileName) {
            return fileName.length > this.maxFileNameLength;
        },
        isFilenameValid(fileName) {
            const regex = /^[-.A-Za-z0-9]*$/;
            return regex.test(fileName);
        },
    },
};
</script>

<style lang="scss" scoped>
.business-order-view-modal {
    &__shipping-documents-row {
        justify-content: space-between;
        margin: 20px;

        @include respond-to($media-tablet, max) {
            justify-content: flex-start;
            margin: 20px 10px;
        }
    }

    &__shipping-document-action {
        margin-left: 34px;
    }

    &__upload-description {
        margin-bottom: 10px;
        padding: 0 15px;
    }
}
</style>
